const MAX_ENTRIES = 20;
const MAX_BODY_LENGTH = 64 * 1024;
const SKIP_URL_PART = "/feedback";

let buffer = [];
let installed = false;

const truncate = (value) => {
  if (value == null) {
    return value;
  }

  const text = typeof value === "string" ? value : safeStringify(value);
  if (text.length <= MAX_BODY_LENGTH) {
    return text;
  }

  return `${text.slice(0, MAX_BODY_LENGTH)}...[truncated]`;
};

const safeStringify = (value) => {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const shouldSkip = (url = "") => String(url).includes(SKIP_URL_PART);

const pushEntry = (entry) => {
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) {
    buffer = buffer.slice(buffer.length - MAX_ENTRIES);
  }
};

const redactHeaders = (headers) => {
  if (!headers) {
    return undefined;
  }

  const result = {};
  Object.keys(headers).forEach((key) => {
    if (/authorization|cookie|password|token/i.test(key)) {
      result[key] = "[redacted]";
    } else {
      result[key] = headers[key];
    }
  });
  return result;
};

export const installNetworkBuffer = (axiosInstance) => {
  if (installed || !axiosInstance) {
    return;
  }

  installed = true;

  axiosInstance.interceptors.request.use((config) => {
    config.metadata = { startTime: Date.now() };
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      const { config } = response;
      if (shouldSkip(config?.url)) {
        return response;
      }

      pushEntry({
        ts: new Date().toISOString(),
        method: (config.method || "get").toUpperCase(),
        url: config.url,
        status: response.status,
        durationMs: config.metadata?.startTime
          ? Date.now() - config.metadata.startTime
          : undefined,
      });

      return response;
    },
    (error) => {
      const config = error.config || {};
      if (shouldSkip(config.url)) {
        return Promise.reject(error);
      }

      const status = error.response?.status;
      const entry = {
        ts: new Date().toISOString(),
        method: (config.method || "get").toUpperCase(),
        url: config.url,
        status: status || 0,
        durationMs: config.metadata?.startTime
          ? Date.now() - config.metadata.startTime
          : undefined,
        error: error.message,
      };

      if (!status || status >= 400) {
        entry.requestBody = truncate(config.data);
        entry.responseBody = truncate(error.response?.data);
        entry.requestHeaders = redactHeaders(config.headers);
      }

      pushEntry(entry);
      return Promise.reject(error);
    },
  );
};

export const getNetworkLogJson = () => JSON.stringify(buffer, null, 2);

export const getNetworkLogBlob = () =>
  new Blob([getNetworkLogJson()], { type: "application/json" });
